import { useQueryClient } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { contentTypesUpdate } from '@/shared/api/generated/content/content';
import type {
  TypeBody,
  WorkflowBody,
  WorkflowStatusBody,
  WorkflowTransitionBody,
} from '@/shared/api/generated/model';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Checkbox } from '@/shared/ui/Checkbox';
import { Input } from '@/shared/ui/Input';
import { Select, SelectItem } from '@/shared/ui/Select';
import { Switch } from '@/shared/ui/Switch';
import { toast } from '@/shared/ui/Toast';

import { StageBadge } from './StageBadge';
import {
  STAGE_COLORS,
  STAGE_PRESETS,
  stageKey,
  statusesOf,
  transitionsOf,
  type StageColor,
} from './stages';
import styles from './Stages.module.scss';

const ROLES = [
  { value: 'admin', label: 'Администратор' },
  { value: 'developer', label: 'Разработчик' },
  { value: 'editor', label: 'Редактор' },
  { value: 'author', label: 'Автор' },
];

const COMMENTS = [
  { value: 'none', label: 'Без комментария' },
  { value: 'optional', label: 'Комментарий по желанию' },
  { value: 'required', label: 'Комментарий обязателен' },
];

/**
 * Набор статусов коллекции: статусы по порядку (так же идут колонки
 * канбана) и разрешённые переходы — кнопки действий в записи. Сохраняется
 * целиком; это часть схемы — на Production переезжает переносом.
 */
export function WorkflowEditor({ type, readOnly = false }: { type: TypeBody; readOnly?: boolean }) {
  const queryClient = useQueryClient();
  const [statuses, setStatuses] = useState<WorkflowStatusBody[]>(statusesOf(type.workflow));
  const [transitions, setTransitions] = useState<WorkflowTransitionBody[]>(
    transitionsOf(type.workflow),
  );
  const [busy, setBusy] = useState(false);

  const keys = statuses.map((s) => s.key);

  const apply = (w: WorkflowBody) => {
    setStatuses(statusesOf(w));
    setTransitions(transitionsOf(w));
  };

  const patchStatus = (index: number, patch: Partial<WorkflowStatusBody>) => {
    setStatuses((list) => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const rename = (index: number, name: string) => {
    patchStatus(index, { name });
  };

  // Ключ меняется — переходы, которые на него ссылаются, следом.
  const rekey = (index: number, key: string) => {
    const old = statuses[index]?.key;

    patchStatus(index, { key });
    setTransitions((list) =>
      list.map((t) => ({
        ...t,
        from: t.from === old ? key : t.from,
        to: t.to === old ? key : t.to,
      })),
    );
  };

  const move = (index: number, by: number) => {
    setStatuses((list) => {
      const next = [...list];
      const [item] = next.splice(index, 1);

      if (item !== undefined) {
        next.splice(index + by, 0, item);
      }

      return next;
    });
  };

  const removeStatus = (index: number) => {
    const key = statuses[index]?.key;

    setStatuses((list) => list.filter((_, i) => i !== index));
    setTransitions((list) => list.filter((t) => t.from !== key && t.to !== key));
  };

  const addStatus = () => {
    const name = `Статус ${String(statuses.length + 1)}`;

    setStatuses((list) => [
      ...list,
      { key: stageKey(name, keys), name, color: 'gray', initial: list.length === 0 },
    ]);
  };

  const patchTransition = (index: number, patch: Partial<WorkflowTransitionBody>) => {
    setTransitions((list) => list.map((t, i) => (i === index ? { ...t, ...patch } : t)));
  };

  const addTransition = () => {
    const from = statuses[0]?.key ?? '';
    const to = statuses[1]?.key ?? from;

    setTransitions((list) => [...list, { from, to, label: 'Дальше', comment: 'none' }]);
  };

  const save = () => {
    setBusy(true);

    contentTypesUpdate(type.id, { workflow: { statuses, transitions } }, { 'If-Match': type.etag })
      .then(() => {
        toast.success(statuses.length === 0 ? 'Статусы убраны' : 'Статусы сохранены');
        void queryClient.invalidateQueries({ queryKey: ['content-type', type.id] });
        void queryClient.invalidateQueries({ queryKey: ['content-types', type.site_id] });
        void queryClient.invalidateQueries({ queryKey: ['records'] });
      })
      .catch((cause: unknown) => {
        toast.error('Не сохранилось', {
          description: cause instanceof ApiError ? cause.detail : 'Попробуйте ещё раз.',
        });
      })
      .finally(() => {
        setBusy(false);
      });
  };

  return (
    <div className={styles['editor']}>
      {statuses.length === 0 && (
        <section className={styles['section']}>
          <h2 className={styles['section__title']}>Статусов пока нет</h2>
          <p className={styles['muted']}>
            Статус — жизненный цикл записи: заявка «новая → в работе → закрыта», отзыв «на проверке
            → одобрен», заказ «новый → оплачен → отправлен». Это не черновик и не публикация — те
            про версию содержимого. Начните с готового набора или с пустого.
          </p>
          {!readOnly && (
            <div className={styles['presets']}>
              {STAGE_PRESETS.map((preset) => (
                <Button
                  key={preset.name}
                  variant="outline"
                  tone="neutral"
                  onClick={() => {
                    apply(preset.workflow);
                  }}
                >
                  {preset.name}
                </Button>
              ))}
            </div>
          )}
        </section>
      )}

      {statuses.length > 0 && (
        <section className={styles['section']}>
          <h2 className={styles['section__title']}>Статусы</h2>
          <p className={styles['muted']}>
            По порядку — так же идут колонки канбана. Новая запись встаёт в начальный. Убранный
            статус: его записи перейдут в начальный.
          </p>
          <div className={styles['rows']}>
            {statuses.map((status, index) => (
              <div key={index} className={styles['row']}>
                <Input
                  value={status.name}
                  disabled={readOnly}
                  aria-label="Название статуса"
                  onChange={(event) => {
                    rename(index, event.target.value);
                  }}
                />
                <Input
                  mono
                  value={status.key}
                  disabled={readOnly}
                  aria-label="Ключ статуса — его видит сайт"
                  onChange={(event) => {
                    rekey(index, event.target.value);
                  }}
                />
                <Select
                  value={status.color ?? 'gray'}
                  disabled={readOnly}
                  aria-label="Цвет"
                  onValueChange={(value) => {
                    patchStatus(index, { color: value as StageColor });
                  }}
                >
                  {STAGE_COLORS.map((color) => (
                    <SelectItem key={color.value} value={color.value}>
                      <StageBadge
                        status={{ key: color.value, name: color.label, color: color.value }}
                      />
                    </SelectItem>
                  ))}
                </Select>
                <Switch
                  checked={status.initial === true}
                  disabled={readOnly}
                  onCheckedChange={(on) => {
                    setStatuses((list) =>
                      list.map((s, i) => ({ ...s, initial: on ? i === index : false })),
                    );
                  }}
                >
                  Начальный
                </Switch>
                {!readOnly && (
                  <div className={styles['actions']}>
                    <Button
                      size="sm"
                      variant="ghost"
                      tone="neutral"
                      aria-label="Выше"
                      disabled={index === 0}
                      iconOnly
                      onClick={() => {
                        move(index, -1);
                      }}
                    >
                      <ArrowUp size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      tone="neutral"
                      aria-label="Ниже"
                      disabled={index === statuses.length - 1}
                      iconOnly
                      onClick={() => {
                        move(index, 1);
                      }}
                    >
                      <ArrowDown size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      tone="danger"
                      aria-label="Убрать статус"
                      iconOnly
                      onClick={() => {
                        removeStatus(index);
                      }}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {!readOnly && (
            <div>
              <Button
                variant="outline"
                tone="neutral"
                iconLeft={<Plus size={15} />}
                onClick={addStatus}
              >
                Статус
              </Button>
            </div>
          )}
        </section>
      )}

      {statuses.length > 1 && (
        <section className={styles['section']}>
          <h2 className={styles['section__title']}>Переходы — кнопки в записи</h2>
          <p className={styles['muted']}>
            Из какого статуса в какой можно перевести и как подписана кнопка. Роли не отмечены —
            может каждый, кто правит записи.
          </p>
          <div className={styles['rows']}>
            {transitions.map((transition, index) => (
              <div key={index} className={styles['transition']}>
                <Select
                  value={transition.from}
                  disabled={readOnly}
                  aria-label="Из статуса"
                  onValueChange={(value) => {
                    patchTransition(index, { from: value });
                  }}
                >
                  {statuses.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      Из «{s.name}»
                    </SelectItem>
                  ))}
                </Select>
                <Select
                  value={transition.to}
                  disabled={readOnly}
                  aria-label="В статус"
                  onValueChange={(value) => {
                    patchTransition(index, { to: value });
                  }}
                >
                  {statuses.map((s) => (
                    <SelectItem key={s.key} value={s.key}>
                      в «{s.name}»
                    </SelectItem>
                  ))}
                </Select>
                <Input
                  value={transition.label}
                  disabled={readOnly}
                  aria-label="Подпись кнопки"
                  placeholder="Подпись кнопки"
                  onChange={(event) => {
                    patchTransition(index, { label: event.target.value });
                  }}
                />
                <Select
                  value={transition.comment ?? 'none'}
                  disabled={readOnly}
                  aria-label="Комментарий"
                  onValueChange={(value) => {
                    patchTransition(index, {
                      comment: value as NonNullable<WorkflowTransitionBody['comment']>,
                    });
                  }}
                >
                  {COMMENTS.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </Select>
                {!readOnly ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    tone="danger"
                    aria-label="Убрать переход"
                    iconOnly
                    onClick={() => {
                      setTransitions((list) => list.filter((_, i) => i !== index));
                    }}
                  >
                    <Trash2 size={14} />
                  </Button>
                ) : (
                  <span />
                )}
                <div className={styles['transition__extra']}>
                  {ROLES.map((role) => {
                    const roles = transition.roles ?? [];
                    const on = roles.includes(role.value);

                    return (
                      <Checkbox
                        key={role.value}
                        size="sm"
                        checked={on}
                        disabled={readOnly}
                        onCheckedChange={() => {
                          patchTransition(index, {
                            roles: on
                              ? roles.filter((r) => r !== role.value)
                              : [...roles, role.value],
                          });
                        }}
                      >
                        {role.label}
                      </Checkbox>
                    );
                  })}
                  <Switch
                    checked={transition.visitor === true}
                    disabled={readOnly}
                    onCheckedChange={(on) => {
                      patchTransition(index, { visitor: on });
                    }}
                  >
                    И посетитель со своей записью
                  </Switch>
                </div>
              </div>
            ))}
          </div>
          {!readOnly && (
            <div>
              <Button
                variant="outline"
                tone="neutral"
                iconLeft={<Plus size={15} />}
                onClick={addTransition}
              >
                Переход
              </Button>
            </div>
          )}
        </section>
      )}

      {!readOnly && (
        <div className={styles['actions']}>
          <Button loading={busy} onClick={save}>
            Сохранить статусы
          </Button>
          {statusesOf(type.workflow).length > 0 && (
            <Button
              variant="ghost"
              tone="danger"
              onClick={() => {
                apply({ statuses: [], transitions: [] });
              }}
            >
              Убрать все статусы
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
